import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Frame } from '../components/Frame';
import { KrabenMark } from '../components/KrabenMark';
import { DAY_ONE, drawSubject, dealForSubject, placeOf } from '../data/sessions';
import { getChallenge, fill } from '../data/exercises';
import { track } from '../analytics';
import { Permission } from '../components/Permission';
import { Darkroom } from '../components/Reveal';
import { keepStory } from '../lib/storyboard';
import { loadProfile } from '../lib/state';
import { transition } from '../lib/transition';
import { askForMic, micPermission, micPlace, openInBrowserUrl, MIC_ESCAPE_MS, MIC_NO_ANSWER_MS, type MicOutcome } from '../lib/mic';
import { loadCampaign } from '../lib/campaign';
import { useViewed } from '../lib/viewed';

type Phase = 'ready' | 'asking' | 'talking' | 'typing' | 'done';

/**
 * Whether the warm-up offers to open itself in Safari or Chrome when Facebook's
 * or Instagram's browser cannot record. Off for now: the link leaves the app
 * the ad opened, the new browser has none of this one's storage, and nobody
 * has measured how many people make it across (lib/mic.ts, openInBrowserUrl).
 * Typing is offered either way; this only adds the link beside it.
 */
const OFFER_OPEN_IN_BROWSER = false;

/** The warm-up's own address in a fresh browser: the campaign comes along, and `from` says which browser it left. */
function warmupHref(from: string): string {
  const c = loadCampaign();
  const u = new URL('/start', window.location.origin);
  const tags: [string, string | undefined][] = [['utm_source', c?.source], ['utm_medium', c?.medium], ['utm_campaign', c?.campaign], ['utm_content', c?.content], ['utm_term', c?.term], ['d', c?.door]];
  for (const [k, v] of tags) if (v) u.searchParams.set(k, v);
  u.searchParams.set('from', from);
  return u.toString();
}

/** The browser that cannot record, named: "Facebook’s browser", or "This browser". */
const whose = (b: string) => (b === 'facebook' ? 'Facebook’s browser' : b === 'instagram' ? 'Instagram’s browser' : 'This browser');


// The subject is on screen from the first second. It used to hold back three
// seconds for effect, and a writer who dived straight in told a fine story about
// something else, then met a follow-up built on a subject she never used
// (interview 3, 2026-09-20).
const REVEAL_AT = 0;
const DIVE_AT = 26; // "Just talk. Dive in." is up from the first second until here: the first "What next?" (owner, 2026-09-18: earlier, and much longer)

/**
 * Day 1's warm-up: tell a story, out loud, to nobody. Tap the mic and the
 * opening line appears ("Once upon a time…"), then the subject, then feeling
 * words drift in to steer you. The recording is kept for the person (it goes
 * up with the day's post, private) and nobody else hears it.
 */
export function Warmup() {
  const nav = useNavigate();
  const session = DAY_ONE;
  const [subject] = useState(() => drawSubject(session));
  const [phase, setPhase] = useState<Phase>('ready');
  useViewed('warmup', phase);
  const [elapsed, setElapsed] = useState(0);
  // the talk can be extended a minute at a time (owner, 2026-09-18)
  const [limit, setLimit] = useState(session.warmup.seconds);
  const [mic, setMic] = useState<'unknown' | 'on' | 'off'>('unknown');
  // Where this is running and whether recording is possible at all, known
  // before Record is shown (lib/mic.ts): Facebook's and Instagram's browsers
  // can lack the microphone entirely, or ask a question nobody ever sees.
  const [place] = useState(micPlace);
  // What the Permissions API says is counted (warmup_mic_check) but not obeyed:
  // a web view can answer "denied" for a microphone its host app would still
  // ask for, and headless Chromium answers "denied" for everything. A real
  // refusal costs one tap — Record, an instant NotAllowedError, the text box.
  const canMic = place.recordable;
  // why the microphone is not being used, for the line over the text box
  const [noMic, setNoMic] = useState<MicOutcome | 'chose' | 'none' | null>(null);
  const [typed, setTyped] = useState('');
  // the asking screen offers a way on once the question has been up a while
  const [stuck, setStuck] = useState(false);
  const giveUp = useRef<AbortController | null>(null);
  const [level, setLevel] = useState(0);
  const [recording, setRecording] = useState<Blob | null>(null);
  // The story is kept the moment it ends, so the crew develop it during the
  // exercise (owner, 2026-09-28: the storyboard is the story's reward).
  const [story, setStory] = useState<{ id?: string; state: 'none' | 'sending' | 'kept' | 'failed' }>({ state: 'none' });
  const [answered, setAnswered] = useState<'on' | 'off' | null>(null);
  const [leftDarkroom, setLeftDarkroom] = useState(false);
  // the last chunk of the recording arrives a beat after the talk stops; hold the screen still until it does
  const [settling, setSettling] = useState(false);
  useEffect(() => {
    if (!settling) return;
    const t = setTimeout(() => setSettling(false), 1500);
    return () => clearTimeout(t);
  }, [settling]);
  const told = useRef({ seconds: 0, subject });
  const startedAt = useRef(0);
  const stream = useRef<MediaStream | null>(null);
  const recorder = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const raf = useRef(0);
  const audioCtx = useRef<AudioContext | null>(null);
  const challenge = getChallenge(session.exerciseId)!;
  const deal = dealForSubject(session, subject);
  const w = session.warmup;

  /** Up to the crew: kept and developed when they are on; when they are off it rides with the post, as before. */
  const send = useCallback((blob: Blob) => {
    setStory({ state: 'sending' });
    keepStory(blob, { day: 1, seconds: told.current.seconds, subject: told.current.subject })
      .then((r) => { setStory({ id: r.storyId, state: 'kept' }); track('story_kept', { status: r.status }); })
      .catch((e) => { setStory({ state: 'failed' }); track('story_keep_failed', { error: String(e).slice(0, 120) }); });
  }, []);

  const stopMic = useCallback(() => {
    cancelAnimationFrame(raf.current);
    const r = recorder.current;
    if (r && r.state !== 'inactive') {
      r.onstop = () => {
        const blob = new Blob(chunks.current, { type: r.mimeType || 'audio/webm' });
        setRecording(blob);
        setSettling(false);
        // with the crew already on, the story goes to them the moment it ends
        if (blob.size > 0 && loadProfile().crew === 'on') send(blob);
        stream.current?.getTracks().forEach((t) => t.stop()); stream.current = null;
      };
      r.stop();
    } else {
      stream.current?.getTracks().forEach((t) => t.stop());
      stream.current = null;
    }
    recorder.current = null;
    audioCtx.current?.close().catch(() => {});
    audioCtx.current = null;
  }, [send]);

  const finish = useCallback((why: 'time' | 'enough' | 'skip') => {
    const secs = Math.round((Date.now() - startedAt.current) / 1000);
    told.current = { seconds: secs, subject };
    stopMic();
    setElapsed(secs);
    if (mic === 'on' && (loadProfile().crew || 'unasked') !== 'off') setSettling(true);
    setPhase('done');
    track('warmup_done', { seconds: secs, mic: mic === 'on', why, subject });
  }, [stopMic, mic, subject]);

  useEffect(() => {
    if (phase !== 'talking') return;
    const id = setInterval(() => {
      const secs = (Date.now() - startedAt.current) / 1000;
      setElapsed(secs);
      if (secs >= limit) finish('time');
    }, 200);
    return () => clearInterval(id);
  }, [phase, limit, finish]);

  // leaving mid-question closes whatever the question brings back later
  useEffect(() => () => { giveUp.current?.abort(); stopMic(); }, [stopMic]);

  // Arriving is its own row: between intro_done and warmup_started sits the one
  // screen that asks a stranger to talk out loud, and it is the likeliest place
  // on Day 1 to put the phone down.
  const shownAt = useRef(Date.now());
  useEffect(() => { track('warmup_shown', { subject }); }, [subject]);
  // Once per visit: what this browser can do, before anybody taps anything.
  // `from` is set when this page was opened by an in-app browser's "open in
  // Safari" link, which is how that escape is counted from the far side.
  useEffect(() => {
    let live = true;
    const from = new URLSearchParams(window.location.search).get('from') || undefined;
    micPermission().then((p) => {
      if (!live) return;
      track('warmup_mic_check', { browser: place.browser, os: place.os, recordable: place.recordable, permission: p, from });
    });
    return () => { live = false; };
  }, [place]);

  /**
   * Typing instead of talking: the way on when the microphone is missing,
   * refused, or never answers, and for anyone who would rather not talk out
   * loud. It was talking-to-nobody before (the clock ran with nothing
   * recording), which left nothing made; a typed story is at least theirs.
   * The clock starts when the box appears.
   */
  const typeIt = (why: MicOutcome | 'chose' | 'none') => {
    giveUp.current?.abort();
    setNoMic(why);
    startedAt.current = Date.now();
    setPhase('typing');
    track('warmup_typing', { why, browser: place.browser, os: place.os, subject });
  };

  /** Out loud to nobody, as it was before the text box: the clock and the nudges, nothing recording. */
  const sayItAnyway = () => {
    giveUp.current?.abort();
    setMic('off');
    setElapsed(0);
    startedAt.current = Date.now();
    setPhase('talking');
    track('warmup_out_loud', { browser: place.browser, os: place.os, subject });
  };

  const start = async () => {
    setElapsed(0);
    setStuck(false);
    setPhase('asking');
    track('warmup_started', { subject, waitMs: Date.now() - shownAt.current });
    const asked = Date.now();
    const where = { browser: place.browser, os: place.os };
    // The question gets a deadline, not a timeout: the screen offers a way on
    // after a few seconds and counts the wait once at fifteen, but a yes that
    // comes late is still taken if the person is still on this screen.
    const ctl = new AbortController();
    giveUp.current = ctl;
    const escape = window.setTimeout(() => setStuck(true), MIC_ESCAPE_MS);
    const waiting = window.setTimeout(() => track('warmup_mic_waiting', { ...where, ms: MIC_NO_ANSWER_MS }), MIC_NO_ANSWER_MS);
    const got = await askForMic({ signal: ctl.signal, onLate: (o) => track('warmup_mic_late', { ...where, outcome: o, ms: Date.now() - asked }) });
    window.clearTimeout(escape); window.clearTimeout(waiting);
    giveUp.current = null;
    const ms = Date.now() - asked;
    // `granted` stays for the funnels already built on it; `outcome` is the whole answer
    if (got.outcome !== 'granted') {
      track('warmup_mic', { granted: false, outcome: got.outcome, ms, error: got.error, ...where });
      setMic('off');
      // no answer means the person has already chosen typing or talking; anything else is offered the text box
      if (got.outcome !== 'no-answer') typeIt(got.outcome);
      return;
    }
    const s = got.stream!;
    try {
      stream.current = s;
      chunks.current = [];
      const r = new MediaRecorder(s);
      r.ondataavailable = (e) => { if (e.data.size) chunks.current.push(e.data); };
      r.start(1000);
      recorder.current = r;
      track('warmup_mic', { granted: true, outcome: 'granted', ms, ...where });
      setMic('on');
      const ctx = new AudioContext();
      audioCtx.current = ctx;
      const src = ctx.createMediaStreamSource(s);
      const an = ctx.createAnalyser();
      an.fftSize = 512;
      src.connect(an);
      const buf = new Uint8Array(an.fftSize);
      const tick = () => {
        an.getByteTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i++) { const v = (buf[i] - 128) / 128; sum += v * v; }
        setLevel(Math.min(1, Math.sqrt(sum / buf.length) * 4));
        raf.current = requestAnimationFrame(tick);
      };
      tick();
    } catch (e) {
      // a yes followed by a recorder that would not start is a fault, not a refusal
      track('warmup_mic_failed', { granted: true, ms, error: (e as Error)?.name, recording: !!recorder.current, ...where });
      if (!recorder.current) {
        // nothing is recording: let the microphone go, and offer the text box
        s.getTracks().forEach((t) => t.stop());
        stream.current = null;
        setMic('off');
        typeIt('failed');
        return;
      }
      // recording, and only the level meter would not start: carry on without it
    }
    // The clock starts only once the microphone question is answered.
    startedAt.current = Date.now();
    setPhase('talking');
  };

  const words = typed.trim().split(/\s+/).filter(Boolean).length;
  const finishTyped = () => {
    const secs = Math.round((Date.now() - startedAt.current) / 1000);
    told.current = { seconds: secs, subject };
    setElapsed(secs);
    setPhase('done');
    track('warmup_done', { seconds: secs, mic: false, typed: true, words, why: 'typed', subject, browser: place.browser, os: place.os });
  };

  const onward = () => {
    if (phase === 'done') track('warmup_onward', { story: story.state, mic: mic === 'on' });
    nav(`/do/${challenge.id}`, { state: { door: 'front', deal, skipDeal: true, reward: true, copy: session.copy, warmup: { seconds: Math.round(elapsed), blob: story.id ? null : recording, storyId: story.id } } });
  };



  const progress = Math.min(1, elapsed / limit);
  const R = 88, C = 2 * Math.PI * R;

  /**
   * The one screen before the microphone: the subject, with nothing running.
   *
   * The subject used to arrive at the same moment as the obligation to be
   * talking: permission resolved, the clock started, and three seconds later
   * the app told you what your story was about. A tester read the prompts out
   * loud and needed time to think (interview 2026-09-18) — and telling a story
   * cold, out loud, into a phone is the hardest thing Day 1 asks of anybody.
   *
   * So the subject is up from the first second, the tap that follows is the one
   * that starts the microphone, and the subject can be swapped as many times as
   * you like: choosing is thinking time that does not feel like waiting. This
   * was two screens until the subject moved onto the first one (owner,
   * 2026-09-25), which left both saying "Tell a story about…" one tap apart.
   */
  if (phase === 'ready') {
    return (
      <Frame theme="fit" left={<span className="tag">Day 1 · Warm-up</span>} right={<span className="tag butter">1 minute</span>}>
        <div className="row between"><span className="label">First, loosen up</span><KrabenMark size={64} /></div>
        {/* The instruction says the subject in so many words: "Once upon a
            time… at the supermarket." on its own read as scenery, and people
            told stories about something else (owner, 2026-09-24). It arrives a
            beat after "Tell a story", and again on every swap. */}
        <h1 className="subject-big story-ask"><span className="story-ask-lead">Tell a story about</span> <span key={subject} className="subject-in">{subject}.</span></h1>
        {/* AUX 25df5d2e, frame K32, T-02: the place he is describing appears
            between his claws. T-01 (story shapes rising from a raised claw) is
            the alternative on the board. */}
        <img className="permission-art warmup-art" src="/art/tell-a-story.jpg" alt="Kraben leaning in to his phone, mouth open mid-sentence, recording a story out loud" />
        <p className="why" style={{ fontSize: 17 }}>Something that happened {placeOf(subject)}, real or made up. You'll have about a minute.</p>
        {/* The reason is the one thing on this screen that makes a stranger
            willing to talk out loud to a phone, so it gets a box of its own
            rather than small print under the button (owner, 2026-09-28). No
            "Something else" either: "Skip the warm-up" is the one way out. */}
        <div className="grew">
          <span className="label" style={{ color: 'var(--green-dark)' }}>Creative reason</span>
          <p className="why" style={{ fontSize: 17 }}>Talking first switches your head from judging to making.</p>
        </div>
        <div className="stack" style={{ marginTop: 'auto', alignItems: 'center' }}>
          {canMic ? (
            <>
              <button className="mic-btn" onClick={start} aria-label="Record">
                <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="3" width="6" height="11" rx="3" fill="currentColor" stroke="none" /><path d="M5 11a7 7 0 0 0 14 0" /><path d="M12 18v3" /><path d="M9 21h6" /></svg>
              </button>
              <span className="counter rec-label"><span className="rec-dot" />Tap to record</span>
              <p className="hint" style={{ textAlign: 'center' }}>Nobody else hears it.</p>
            </>
          ) : (
            <>
              {/* Nothing to record with — no microphone API or no recorder, as
                  in an in-app browser whose app never asked for the microphone —
                  so the button that could only disappoint is not shown at all,
                  and the story is typed. */}
              <button className="btn coral" onClick={() => typeIt('none')}>Type your story</button>
              <p className="hint" style={{ textAlign: 'center' }}>{whose(place.browser)} can’t use the microphone, so write it instead. Nobody else reads it.</p>
            </>
          )}
          {/*
            The way past the microphone, before it is ever tapped. Somebody
            unwilling to grant or refuse it — on a train, in an office, or just
            not ready to talk out loud on minute one — would otherwise have no
            way on at all. This goes straight to the exercise: the bridge screen
            after the warm-up would tell them they had told a story, which they
            have not.

            Inside Facebook's and Instagram's browsers the microphone is a
            gamble (lib/mic.ts), so typing is offered beside it before anyone
            finds out. Everywhere else it appears only once the microphone has
            let them down, which keeps this screen the one decision it was.
          */}
          <div className="row" style={{ justifyContent: 'center', gap: 18 }}>
            {canMic && place.browser !== 'browser' && <button className="link" onClick={() => typeIt('chose')}>Type it instead</button>}
            <button className="link" onClick={() => { track('warmup_skipped', { subject, browser: place.browser }); onward(); }}>Skip the warm-up</button>
          </div>
          {!canMic && <OpenInBrowser place={place} />}
        </div>
      </Frame>
    );
  }

  /**
   * Waiting on the microphone question. In Safari and Chrome it is a sheet the
   * person answers in a second. In an in-app browser it can be a question that
   * never appears, and this screen used to wait for it forever with nothing to
   * tap (2026-10-15, Facebook on Android: 43 seconds, then gone). So after a
   * few seconds it says so and offers the two ways on without it.
   */
  if (phase === 'asking') {
    return (
      <Frame left={<span className="tag">Day 1 · Warm-up</span>}>
        <div className="talk">
          <div className="ring-wrap"><svg className="ring" viewBox="0 0 200 200" width="220" height="220"><circle cx="100" cy="100" r={R} fill="none" stroke="var(--rule)" strokeWidth="10" /></svg><div className="ring-centre"><span className="label">One moment</span></div></div>
          <p className="why" style={{ textAlign: 'center' }}>Your phone is asking about the microphone. Either answer is fine.</p>
        </div>
        {stuck && (
          <div className="stack" style={{ marginTop: 'auto' }}>
            <p className="hint" style={{ textAlign: 'center' }}>Nothing showing up? Some apps’ browsers never ask.</p>
            <button className="btn" onClick={() => typeIt('no-answer')}>Type it instead</button>
            <button className="link" onClick={sayItAnyway}>Say it out loud anyway</button>
          </div>
        )}
      </Frame>
    );
  }

  /**
   * The story, typed. Same subject, same promise, a box instead of a clock.
   * The words stay on this phone and out of session replays (`data-private`,
   * analytics.ts): "Nobody else hears it" is the promise the warm-up makes,
   * and a typed story keeps it. They are not sent to the crew — a storyboard
   * is made from a recording (POST /api/kindling/stories takes audio only).
   */
  if (phase === 'typing') {
    const why = noMic === 'unavailable' || noMic === 'failed' ? 'The microphone isn’t working here, and that is fine.' : noMic === 'denied' || noMic === 'no-answer' ? 'No microphone, and that is fine.' : null;
    return (
      <Frame left={<span className="tag">Day 1 · Warm-up</span>} right={<span className="tag">{subject}</span>}>
        {why && <p className="hint">{why}</p>}
        <h1 className="story-ask"><span className="story-ask-lead">Tell a story about</span> {subject}.</h1>
        <div className="stack" data-private>
          <textarea className="input big" rows={7} value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="Once upon a time…" aria-label="Your story" />
          <div className="row between">
            <span className="counter">{words} {words === 1 ? 'word' : 'words'}</span>
            <span className="hint">Nobody else reads it.</span>
          </div>
        </div>
        <div className="stack" style={{ marginTop: 'auto' }}>
          <button className="btn" onClick={finishTyped} disabled={words === 0}>Done</button>
          <div className="row" style={{ justifyContent: 'center', gap: 18 }}>
            {/* the other ways, never a dead end: record after all where it might work, else say it out loud */}
            {noMic === 'chose' && canMic ? <button className="link" onClick={start}>Record instead</button> : <button className="link" onClick={sayItAnyway}>Say it out loud instead</button>}
            <button className="link" onClick={() => { track('warmup_skipped', { subject, browser: place.browser, from: 'typing' }); onward(); }}>Skip the warm-up</button>
          </div>
          {place.browser !== 'browser' && noMic !== 'chose' && <OpenInBrowser place={place} />}
        </div>
      </Frame>
    );
  }

  if (phase === 'talking') {
    const canStop = elapsed >= w.minSeconds;
    const revealed = elapsed >= REVEAL_AT;
    // Only ever one of three things: keep talking, what next, how did it end.
    // The end one takes the last 15 seconds, so it follows "Another minute".
    const ending = elapsed >= limit - w.every;
    const nudging = elapsed >= DIVE_AT && (elapsed - DIVE_AT) % w.every < w.nudge;
    const say = ending ? w.prompts.end : nudging ? w.prompts.next : w.prompts.keep;
    return (
      <Frame left={<span className="tag">Day 1 · Warm-up</span>} right={revealed ? <span className="tag">{subject}</span> : undefined}>
        <div className="talk">
          <div className="ring-wrap">
            <svg className="ring" viewBox="0 0 200 200" width="220" height="220">
              <circle cx="100" cy="100" r={R} fill="none" stroke="var(--rule)" strokeWidth="10" />
              <circle cx="100" cy="100" r={R} fill="none" stroke="var(--green)" strokeWidth="10" strokeLinecap="round" strokeDasharray={C} strokeDashoffset={C * (1 - progress)} transform="rotate(-90 100 100)" />
            </svg>
            <div className="level" style={{ transform: `scale(${1 + level * 0.9})`, opacity: mic === 'on' ? 0.55 : 0.2 }} />
            <div className="ring-centre">
              <span key={say} className={`word prompt ${say === w.prompts.keep ? 'quiet' : ''}`}>{say}</span>
            </div>
          </div>
          {/* the opening line never leaves: you are telling a story, and the
              subject drops in under it and stays there too (owner, 2026-09-18) */}
          <div className={`story ${revealed ? 'on' : ''}`}>
            <div className="reveal">Once upon a time…</div>
            {revealed && <div key="subject" className="reveal subject">…{placeOf(subject)}.</div>}
          </div>
          {elapsed < DIVE_AT && <p className="why dive" style={{ textAlign: 'center', fontSize: 18 }}>A story about {subject}. Just talk, whatever comes.</p>}
          {mic === 'off' && <p className="hint" style={{ textAlign: 'center' }}>No microphone, and that is fine. Say it out loud anyway.</p>}
        </div>
        <div className="stack" style={{ marginTop: 'auto' }}>
          <button className="btn" onClick={() => finish('enough')} disabled={!canStop} style={{ background: canStop ? undefined : 'var(--rule)', boxShadow: canStop ? undefined : 'none', color: canStop ? undefined : 'var(--muted)' }}>
            {canStop ? 'Done' : `Keep going (${Math.max(0, Math.ceil(w.minSeconds - elapsed))} s)`}
          </button>
          <div className="row" style={{ justifyContent: 'center', gap: 18 }}>
            {/* always there: it only used to show in the last 20 seconds, and
                people finished before they ever saw it (owner, 2026-09-21) */}
            <button className="link" onClick={() => { setLimit(limit + 60); track('warmup_extended', { limit: limit + 60 }); }}>Another minute</button>
            <button className="link" onClick={() => finish('skip')}>Stop</button>
          </div>
        </div>
      </Frame>
    );
  }

  const crew = answered ?? (loadProfile().crew || 'unasked');
  const hasStory = !!recording && recording.size > 0;
  if (phase === 'done' && settling && !recording) return <div className="stage" style={{ background: '#2b2a44' }} />;
  if (phase === 'done' && hasStory && crew === 'unasked') {
    // Asked here now, not after the first card: the story is the first thing the crew could work on.
    return <Permission day={1} onAnswer={(a) => { if (a === 'on' && recording) send(recording); transition(() => setAnswered(a)); }} />;
  }
  if (phase === 'done' && story.state !== 'none' && story.state !== 'failed' && !leftDarkroom) {
    return <Darkroom day={1} words={`Once upon a time… ${placeOf(subject)}.`} onLeave={() => { track('story_darkroom_left', { kept: story.state }); transition(() => setLeftDarkroom(true)); }} />;
  }

  return (
    <Frame left={<span className="tag">Day 1</span>} right={<span className="tag butter">{challenge.minutes} minutes</span>}>
      <div className="row between">
        <h1>Warm-up done.</h1>
        <KrabenMark size={72} />
      </div>
      {/* This screen used to say "N seconds, out loud, about {subject}", which
          the app cannot know: a writer who told a story about something else was
          told she hadn't (interview 3). So it says what comes next, and that the
          place carries over on purpose. */}
      <p className="why" style={{ fontSize: 17 }}>{story.state === 'none' || story.state === 'failed' ? 'You’re warmed up.' : 'Your crew are developing your story.'} Next is today’s exercise: a few minutes, set in the same place as your story.</p>
      <div className="sheet">
        <span className="label">Now</span>
        <h2>{fill(session.copy.prompt, deal)}</h2>
        <p className="why">{session.copy.hint}</p>
      </div>
      <div className="stack" style={{ marginTop: 'auto' }}>
        {/* the story must be kept (or have failed) first, or it would be sent twice: once now, once with the post */}
        <button className="btn coral" onClick={onward} disabled={story.state === 'sending'}>{story.state === 'sending' ? 'One moment' : 'Begin'}</button>
      </div>
    </Frame>
  );
}

/**
 * "Open in Safari" (or Chrome), behind OFFER_OPEN_IN_BROWSER: a plain link the
 * person chooses, never a redirect, shown only inside Facebook's or
 * Instagram's browser once the microphone is known not to work there. Counted
 * on tap here and on arrival on the far side (`from` on warmup_mic_check).
 */
function OpenInBrowser({ place }: { place: ReturnType<typeof micPlace> }) {
  if (!OFFER_OPEN_IN_BROWSER) return null;
  const href = openInBrowserUrl(place, warmupHref(place.browser));
  if (!href) return null;
  return (
    <a className="link" style={{ textAlign: 'center' }} href={href} onClick={(e) => {
      track('warmup_open_browser', { browser: place.browser, os: place.os });
      // Facebook on iOS reportedly hands x-safari-https to Safari only from window.open
      if (place.os === 'ios') { e.preventDefault(); window.open(href, '_blank'); }
    }}>
      {place.os === 'ios' ? 'Open in Safari to record' : 'Open in Chrome to record'}
    </a>
  );
}
